
import {
  defineComponent, Ref, ref, computed, onMounted, onUnmounted,
} from 'vue';
import VueTagsInput from '@sipec/vue3-tags-input';
import { ISearchLabelInput, ITagInput } from '@/types/interfaces';
import AppLabel from '../AppLabel.vue';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import InputLabelsService from '@/services/InputLabelsService';
import AppSmallLoader from './AppSmallLoader.vue';

export default defineComponent({
  components: {
    VueTagsInput,
    AppLabel,
    AppSmallLoader,
  },
  props: {
    isAppMediaView: { type: Boolean, default: false },
    isFile: { type: Boolean, default: false },
    isPhoto: { type: Boolean, default: false },
    showDoneControl: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
    focus: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const inputElement = ref();
    const recentLabelsList = computed(() => mainStore.getRecentInputLabels(props.isFile, props.isPhoto));

    const isLabelSaving = ref(false);

    const isFocused: Ref<boolean> = ref(false);
    const tag: Ref<string> = ref('');

    const preventBlur: Ref<boolean> = ref(false);

    const showInfoBox = computed(() => !tag.value);

    const toBeDisabled = computed(() => {
      if (props.isAppMediaView) return false;
      return (!mainStore.inputPhotoTags.length && !mainStore.inputPhotoViewTags.length) || isLabelSaving.value;
    });

    function focusInput() {
      const inpEl = document.getElementById('inpEl'); // using getElementById cause vue ref has errors
      if (inpEl) {
        // inputElement.value.newTagInput.focus();
        inpEl.focus();
        inpEl.scrollTop = 0;
      }
    }

    function onPreventBlur() {
      preventBlur.value = true;
    }
    function onFocus() {
      emit('on-focus');
      isFocused.value = true;
    }
    function onBlur() {
      // using setTimeout to trigger function after onPreventBlur()
      setTimeout(() => {
        if (preventBlur.value) {
          preventBlur.value = false;
          focusInput();
        } else {
          isFocused.value = false;
        }
        tag.value = '';
      }, 300);
    }

    function updateTags(newTags: ITagInput[]) {
      if (props.isAppMediaView) {
        mainStore.inputPhotoViewTags = newTags;
      }
      if (props.isPhoto) {
        mainStore.inputPhotoTags = newTags;
      } else if (props.isFile) {
        mainStore.inputFileTags = newTags;
      } else {
        mainStore.inputCaseTags = newTags;
      }
      emit('on-update-tags');
    }

    function addTag(label: ISearchLabelInput) {
      if (props.skeleton) {
        return;
      }
      tag.value = '';

      if (!label.id && !label.exists) {
        isLabelSaving.value = true;
        InputLabelsService.createInputLabel(label.name).then((response) => {
          if (response.success) {
            mainStore.recentInputLabels.push(response.results.data);
          }
        }).finally(() => { isLabelSaving.value = false; });
      }

      if (props.isAppMediaView) {
        if (mainStore.inputPhotoViewTags.some((el) => el.text === label.name)) return;
        mainStore.inputPhotoViewTags.push({ text: label.name });
      }
      if (props.isPhoto) {
        if (mainStore.inputPhotoTags.some((el) => el.text === label.name)) return;
        mainStore.inputPhotoTags.push({ text: label.name });
      } else if (props.isFile) {
        if (mainStore.inputFileTags.some((el) => el.text === label.name)) return;
        mainStore.inputFileTags.push({ text: label.name });
      } else {
        if (mainStore.inputCaseTags.some((el) => el.text === label.name)) return;
        mainStore.inputCaseTags.push({ text: label.name });
      }
      emit('on-update-tags');
      focusInput();
    }

    let timeout: ReturnType<typeof setTimeout>;
    function searchInputLabels(e) {
      if (e.keyCode === 13) return;
      clearTimeout(timeout);
      if (tag.value === '') {
        mainStore.searchInputLabels = [];
      } else {
        timeout = setTimeout(() => {
          mainStore.onSearchInputLabels(tag.value).then((labels) => {
            const hasSearchLable = labels.find((lab) => lab.name === tag.value);
            if (!hasSearchLable) {
              mainStore.searchInputLabels = [{ name: tag.value }, ...mainStore.searchInputLabels];
            }
          });
        }, 300);
      }
    }

    function inputDone() {
      if (isLabelSaving.value) {
        return;
      }
      emit('on-input-done');
    }

    function saveTag(v) {
      const inpTag = v.tag.text as string;
      const labelExists = !!mainStore.recentInputLabels.filter((el) => el.name === inpTag).length;
      if (labelExists) {
        addTag({ name: inpTag });
      } else {
        InputLabelsService.createInputLabel(inpTag).then((response) => {
          if (response.success) {
            mainStore.recentInputLabels.push(response.results.data);
            addTag({ name: inpTag, exists: true });
          }
        });
      }
    }

    function deleteTag(v) {
      emit('on-label-delete', v.tag.text);
      v.deleteTag();
      focusInput();
    }

    onMounted(() => {
      if (props.focus) {
        focusInput();
      }
      mainStore.inputPhotoViewTags = [];
      // mainStore.inputPhotoTags = [];
      // mainStore.inputFileTags = [];
      // mainStore.inputCaseTags = [];
      if (driveStore.appPhotoViewMedia?.labels?.length) {
        driveStore.appPhotoViewMedia.labels.forEach((el) => {
          mainStore.inputPhotoViewTags.push({ text: el.name });
          // 1. search index of a tag in recents mainStore.recentInputLabels
          // const index = mainStore.recentInputLabels.findIndex((object) => object.id === el.id);

          // 2. slice it from recent if present
          // mainStore.recentInputLabels.splice(index, 1);
        });
      }
    });

    onUnmounted(() => {
      mainStore.inputPhotoViewTags = [];
    });

    return {
      inputElement,
      mainStore,
      tag,
      showInfoBox,
      isLabelSaving,
      recentLabelsList,
      updateTags,
      onFocus,
      onBlur,
      onPreventBlur,
      addTag,
      searchInputLabels,
      inputDone,
      saveTag,
      deleteTag,
      toBeDisabled,
    };
  },
});
